/**
 * Google Expressive Pass Preview
 *
 * Replaces GooglePassFront for the new scrollable Google Wallet pass design.
 * GooglePassFront remains in use as the "classic" view when users revert.
 */
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { getContrastYIQ } from '../../../../../utils/colorBasedOnBgContrast';
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MeContext } from '../../../../../context/MeContext';
import { MeContextState } from '../../../../../store/types/account';
import previewWatermarkLight from '../../../../../assets/vectors/previewWatermarkLight.svg';
import previewWatermarkDark from '../../../../../assets/vectors/previewWatermarkDark.svg';
import { TrialBannerOverlay } from '../shared/TrialBannerOverlay';
import { ImagePlaceholder } from '../shared/ImagePlaceholder';
import {
  ArrowForwardIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons';
import { PreviewData, TemplateType } from '../../../../../store/types/template';
import { PassNotification } from '../shared/PassNotification';
import {
  getGoogleAppLinkHref,
  getGoogleBackgroundColor,
  getGoogleSecValuesPresent,
  getGoogleAuxValuesPresent,
  getGoogleFirstRowCount,
  getGoogleSecondRowCount,
  getGoogleRowAlignments,
  getBarcodePreviewConfig,
  getGoogleWatermarkConfig,
} from '../../utils/googlePassPreview';
import { getImageDimensions } from '../../utils/imageDimensions';
import { GooglePassField } from './GooglePassField';
import BarcodePreview from '../BarcodePreview';
import googlePlaceholderText from '../../../../../assets/vectors/googlePlaceholderText.svg';
import { GOOGLE_DEFAULT_WAVE_BANNER_BASE64 } from '../../../../../utils/base64';
import parseString from '../../../../../hooks/useLinkFormatter';
import { GooglePhoneHeader } from './GooglePhoneHeader';
import { GoogleNfcBadge } from './GoogleNfcBadge';
import { GooglePassFooter } from './GooglePassFooter';
import { GoogleAppLinkButton } from './GoogleAppLinkButton';

export const HIGHLIGHT_COLOR = '#FF4935';

export const highlightProps = (isHighlighted: boolean | undefined) => ({
  position: 'relative' as const,
  'data-highlighted': isHighlighted || undefined,
  _after: isHighlighted
    ? {
        content: '""',
        position: 'absolute',
        inset: '-3px',
        border: `2px solid ${HIGHLIGHT_COLOR}`,
        borderRadius: '4px',
        pointerEvents: 'none',
      }
    : undefined,
});

const GoogleExpressivePassPreview = ({
  previewData,
  createTemplate,
  notifOpen,
  templatesPage = false,
  scrollContainerRef,
  onScroll,
}: {
  previewData: PreviewData;
  createTemplate: boolean;
  notifOpen: boolean;
  templatesPage?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}) => {
  const { state: meState } = useContext(MeContext) as { state: MeContextState };
  const watermarkEnabled = meState.watermarkEnabled;
  const isInTrial = meState.organisationStatus === 'trial';

  const isWatermarkFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('design.shared.watermarkColor') ||
      previewData.isFieldHighlighted?.('design.google.watermarkColor') ||
      previewData.isFieldHighlighted?.('design.shared.watermarkPosition') ||
      previewData.isFieldHighlighted?.('design.google.watermarkPosition')
    );
  }, [previewData.isFieldHighlighted]);

  const isBarcodeFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('barcode.enabled') ||
      previewData.isFieldHighlighted?.('barcode.type') ||
      previewData.isFieldHighlighted?.('barcode.source') ||
      previewData.isFieldHighlighted?.('barcode.value') ||
      previewData.isFieldHighlighted?.('barcode.displayText')
    );
  }, [previewData.isFieldHighlighted]);

  const [isBannerHovered, setIsBannerHovered] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>(
    {}
  );

  // Auto-scroll to highlighted field
  useEffect(() => {
    if (!scrollContainerRef?.current || !previewData.focusedField) return;
    const container = scrollContainerRef.current;
    const timer = setTimeout(() => {
      const highlighted = container.querySelector(
        '[data-highlighted="true"]'
      ) as HTMLElement | null;
      if (highlighted) {
        const containerRect = container.getBoundingClientRect();
        const elRect = highlighted.getBoundingClientRect();
        const isVisible =
          elRect.top >= containerRect.top + 20 &&
          elRect.bottom <= containerRect.bottom - 20;
        if (!isVisible) {
          const scrollOffset =
            elRect.top -
            containerRect.top +
            container.scrollTop -
            container.clientHeight / 2 +
            highlighted.clientHeight / 2;
          container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
        }
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [previewData.focusedField, scrollContainerRef]);

  // background + contrast
  const backgroundColor = useMemo(
    () => getGoogleBackgroundColor(previewData),
    [previewData.passDesign?.backgroundColor, previewData.passBackgroundColor]
  );

  const getContrastColor = useMemo(
    () => getContrastYIQ(backgroundColor),
    [backgroundColor]
  );

  const lightenedBg = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const isLight = (r + g + b) / 3 > 230;
    const factor = isLight ? 0.015 : 0.15;
    const lr = isLight
      ? Math.round(r - r * factor)
      : Math.min(255, Math.round(r + (255 - r) * factor));
    const lg = isLight
      ? Math.round(g - g * factor)
      : Math.min(255, Math.round(g + (255 - g) * factor));
    const lb = isLight
      ? Math.round(b - b * factor)
      : Math.min(255, Math.round(b + (255 - b) * factor));
    return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
  }, [backgroundColor]);

  // barcode (image + dimensions)
  const {
    image: barcodeImage,
    width: previewBarcodeImageWidth,
    height: previewBarcodeImageHeight,
  } = useMemo(
    () => getBarcodePreviewConfig(previewData),
    [previewData?.barcode?.type]
  );

  const mapAlignToJustify = (a: 'left' | 'center' | 'right') =>
    a === 'left' ? 'flex-start' : a === 'center' ? 'center' : 'flex-end';

  const bannerSrcForRatio =
    previewData.googleBannerImage || previewData.bannerImage || null;

  const defaultRatio = useMemo(() => {
    const { width, height } = getImageDimensions(
      previewData.passType as TemplateType
    ).google.banner;
    return String(height === 0 ? 0 : width / height);
  }, [previewData.passType]);

  const [googleBannerAspectRatio, setGoogleBannerAspectRatio] =
    useState(() => {
      if (bannerSrcForRatio) {
        const img = document.createElement('img');
        img.src = bannerSrcForRatio;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          return String(img.naturalWidth / img.naturalHeight);
        }
      }
      return defaultRatio;
    });

  useEffect(() => {
    if (!bannerSrcForRatio) {
      setGoogleBannerAspectRatio(defaultRatio);
      return;
    }
    const img = document.createElement('img');
    img.src = bannerSrcForRatio;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setGoogleBannerAspectRatio(
        String(img.naturalWidth / img.naturalHeight)
      );
      return;
    }
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setGoogleBannerAspectRatio(
          String(img.naturalWidth / img.naturalHeight)
        );
      }
    };
    return () => { img.onload = null; };
  }, [bannerSrcForRatio, defaultRatio]);

  // Fallback: if the canvas check fails (CORS), check if the image is a PNG/WebP (can have transparency)
  const shouldShowBanner =
    previewData.passType === 'transit'
      ? !!previewData.googleBannerImage
      : previewData.googleBannerImage
        ? previewData.googleBannerImage
        : !!previewData.bannerImage;

  const showsBannerPlaceholder =
    !shouldShowBanner && previewData.passType !== 'transit';

  const isDefaultWaveBanner =
    shouldShowBanner &&
    (previewData.googleBannerImage === GOOGLE_DEFAULT_WAVE_BANNER_BASE64 ||
      previewData.bannerImage === GOOGLE_DEFAULT_WAVE_BANNER_BASE64);

  const hasAppLink =
    !!previewData.googleAppLinkPlayStoreUrl ||
    !!previewData.googleAppLinkPackageName ||
    !!previewData.googleAppLinkWebAppUrl;

  const previewBarcodeImage = barcodeImage;

  const secValuesPresent = useMemo(
    () => getGoogleSecValuesPresent(previewData),
    [
      previewData.detailsOneId,
      previewData.detailsOneLabel,
      previewData.detailsOneValue,
      previewData.detailsTwoId,
      previewData.detailsTwoLabel,
      previewData.detailsTwoValue,
      previewData.detailsThreeId,
      previewData.detailsThreeLabel,
      previewData.detailsThreeValue,
    ]
  );

  const firstRowCount = getGoogleFirstRowCount(previewData);
  const secondRowCount = getGoogleSecondRowCount(previewData);
  const singleDetailGeneric =
    previewData.passType === 'generic' && firstRowCount <= 1 && secondRowCount === 0;

  const auxValuesPresent = useMemo(
    () => getGoogleAuxValuesPresent(previewData),
    [
      previewData.passType,
      previewData.detailsFourId,
      previewData.detailsFourLabel,
      previewData.detailsFourValue,
      previewData.detailsFiveId,
      previewData.detailsFiveLabel,
      previewData.detailsFiveValue,
      previewData.detailsSixId,
      previewData.detailsSixLabel,
      previewData.detailsSixValue,
    ]
  );

  const detailsFieldsPresent = secValuesPresent || auxValuesPresent;

  const firstRowLabelsCount = useMemo(
    () => getGoogleFirstRowCount(previewData),
    [
      previewData.detailsOneId,
      previewData.detailsOneLabel,
      previewData.detailsOneValue,
      previewData.detailsTwoId,
      previewData.detailsTwoLabel,
      previewData.detailsTwoValue,
      previewData.detailsThreeId,
      previewData.detailsThreeLabel,
      previewData.detailsThreeValue,
    ]
  );

  const secondRowLabelsCount = useMemo(
    () => getGoogleSecondRowCount(previewData),
    [
      previewData.detailsFourId,
      previewData.detailsFourLabel,
      previewData.detailsFourValue,
      previewData.detailsFiveId,
      previewData.detailsFiveLabel,
      previewData.detailsFiveValue,
      previewData.detailsSixId,
      previewData.detailsSixLabel,
      previewData.detailsSixValue,
    ]
  );

  const {
    secondAlignment: detailsTwoAlignment,
    thirdAlignment: detailsThreeAlignment,
  } = useMemo(
    () =>
      getGoogleRowAlignments(
        firstRowLabelsCount,
        [
          previewData.detailsOneId,
          previewData.detailsTwoId,
          previewData.detailsThreeId,
        ],
        [
          previewData.detailsOneLabel,
          previewData.detailsTwoLabel,
          previewData.detailsThreeLabel,
        ],
        [
          previewData.detailsOneValue,
          previewData.detailsTwoValue,
          previewData.detailsThreeValue,
        ]
      ),
    [
      firstRowLabelsCount,
      previewData.detailsOneId,
      previewData.detailsTwoId,
      previewData.detailsThreeId,
      previewData.detailsOneLabel,
      previewData.detailsTwoLabel,
      previewData.detailsThreeLabel,
      previewData.detailsOneValue,
      previewData.detailsTwoValue,
      previewData.detailsThreeValue,
    ]
  );

  const {
    secondAlignment: detailsFiveAlignment,
    thirdAlignment: detailsSixAlignment,
  } = useMemo(
    () =>
      getGoogleRowAlignments(
        secondRowLabelsCount,
        [
          previewData.detailsFourId,
          previewData.detailsFiveId,
          previewData.detailsSixId,
        ],
        [
          previewData.detailsFourLabel,
          previewData.detailsFiveLabel,
          previewData.detailsSixLabel,
        ],
        [
          previewData.detailsFourValue,
          previewData.detailsFiveValue,
          previewData.detailsSixValue,
        ]
      ),
    [
      secondRowLabelsCount,
      previewData.detailsFourId,
      previewData.detailsFiveId,
      previewData.detailsSixId,
      previewData.detailsFourLabel,
      previewData.detailsFiveLabel,
      previewData.detailsSixLabel,
      previewData.detailsFourValue,
      previewData.detailsFiveValue,
      previewData.detailsSixValue,
    ]
  );

  const { color: watermarkColor, position: watermarkPosition } = useMemo(
    () => getGoogleWatermarkConfig(previewData),
    [
      previewData.googleBannerImage,
      previewData.googleBannerWatermarkColor,
      previewData.googleBannerWatermarkPosition,
      previewData.watermarkColor,
      previewData.watermarkPosition,
    ]
  );

  return (
    <>
      {/* NOTIFICATION */}
      {!templatesPage && (
        <PassNotification
          open={notifOpen}
          autoHideMs={0}
          title={previewData?.notificationHeader || 'Notification header'}
          subtitle={
            previewData?.notificationMessage ?? 'Your message text here'
          }
          imageUrl={previewData.googleLogoImage || previewData.iconLogo}
          inset="12% 3.5% auto 3%"
          device="google"
        />
      )}

      {/* PASS CONTENT */}
      <Box
        ref={scrollContainerRef}
        onScroll={onScroll}
        position={templatesPage ? 'static' : 'absolute'}
        top={templatesPage ? undefined : '5.8%'}
        left={templatesPage ? undefined : '4%'}
        right={templatesPage ? undefined : '5%'}
        bottom={templatesPage ? undefined : '2.8%'}
        bg={backgroundColor}
        borderRadius={templatesPage ? '0' : '43px'}
        overflowY={templatesPage ? 'visible' : 'auto'}
        overflowX="hidden"
        display="flex"
        flexDirection="column"
        isolation="isolate"
        sx={{
          scrollbarWidth: 'none',
          overscrollBehaviorY: 'none',
          WebkitOverflowScrolling: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          position="relative"
          bg={backgroundColor}
          borderRadius={templatesPage ? '25px' : '0'}
          borderTopRadius={templatesPage ? '25px' : '0'}
          borderBottomRadius={templatesPage ? '25px' : '18px'}
          overflow="hidden"
          border={
            templatesPage && !shouldShowBanner ? '1px solid #D3D3D3' : 'none'
          }
          w="100%"
          maxW="full"
          flexShrink={0}
        >
          {!templatesPage && (
            <GooglePhoneHeader
              contrastColor={getContrastColor}
              showQrIcon={
                !!(previewData.googleNfcEnabled && previewData.barcode?.enabled)
              }
            />
          )}

          {/* ICON */}
          <Flex
            pt="18px"
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              mb={previewData.googleWideLogoImage ? '8px' : undefined}
              {...(() => {
                const isHighlighted =
                  previewData.isFieldHighlighted?.('design.shared.icon') ||
                  previewData.isFieldHighlighted?.('design.google.icon') ||
                  previewData.isFieldHighlighted?.('design.shared.logo') ||
                  previewData.isFieldHighlighted?.('design.google.logo') ||
                  previewData.isFieldHighlighted?.('design.google.wideLogo');
                return {
                  outline: isHighlighted
                    ? `2px solid ${HIGHLIGHT_COLOR}`
                    : 'none',
                  outlineOffset: '4px',
                  borderRadius: '4px',
                  'data-highlighted': isHighlighted || undefined,
                };
              })()}
            >
              {(() => {
                const wideLogoImage = previewData.googleWideLogoImage;
                const logoImage =
                  previewData.googleLogoImage || previewData.iconLogo;

                if (wideLogoImage) {
                  return (
                    <Image
                      maxW="120px"
                      maxH="38px"
                      objectFit="contain"
                      src={wideLogoImage}
                      alt=""
                    />
                  );
                }

                if (logoImage) {
                  return (
                    <Box
                      borderRadius="100%"
                      boxSize="67px"
                      border="1.5px solid"
                      borderColor={
                        getContrastColor === '#000000'
                          ? 'rgba(0,0,0,0.09)'
                          : 'rgba(255,255,255,0.13)'
                      }
                      p="0"
                    >
                      <Box
                        borderRadius="100%"
                        boxSize="100%"
                        bg="white"
                        overflow="hidden"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Image
                          borderRadius="100%"
                          boxSize="100%"
                          objectFit="cover"
                          src={logoImage}
                          alt=""
                        />
                      </Box>
                    </Box>
                  );
                } else {
                  return (
                    <ImagePlaceholder
                      height="65px"
                      width="65px"
                      borderRadius="100%"
                    />
                  );
                }
              })()}
            </Box>

            {/* DESCRIPTION — hidden when wide logo is displayed */}
            {!previewData.googleWideLogoImage && (
              <Text
                fontSize="11px"
                textStyle="headingFamilySemiBoldGooglePay"
                textAlign="center"
                color={getContrastColor}
                mt="14px"
                outline={
                  previewData.isFieldHighlighted?.('description')
                    ? `2px solid ${HIGHLIGHT_COLOR}`
                    : 'none'
                }
                outlineOffset="4px"
                borderRadius="4px"
                data-highlighted={
                  previewData.isFieldHighlighted?.('description') || undefined
                }
              >
                {previewData.description
                  ? previewData.description
                  : 'Pass Description'}
              </Text>
            )}

            {/* CENTRAL TITLE / GOOGLE PASS TITLE */}
            {previewData.passType === 'generic' ? (
              <>
                {previewData.primaryOneValue && (
                  <Text
                    fontSize="24px"
                    fontWeight="800"
                    textStyle="headingFamilyRegularGooglePay"
                    textAlign="center"
                    color={getContrastColor}
                    mt="6px"
                    px="4px"
                    wordBreak="break-word"
                    {...highlightProps(
                      previewData.isFieldHighlighted?.(
                        'central.one.defaultValue'
                      )
                    )}
                  >
                    {previewData.primaryOneValue}
                  </Text>
                )}
                {previewData.primaryOneLabel && (
                  <Text
                    fontSize="11px"
                    textStyle="headingFamilySemiBoldGooglePay"
                    textAlign="center"
                    color={getContrastColor}
                    mt="6px"
                    {...highlightProps(
                      previewData.isFieldHighlighted?.('central.one.label')
                    )}
                  >
                    {previewData.primaryOneLabel}
                  </Text>
                )}
              </>
            ) : (
              <Text
                fontSize="24px"
                fontWeight="800"
                textStyle="headingFamilyRegularGooglePay"
                textAlign="center"
                color={getContrastColor}
                mt="6px"
                px="15px"
                wordBreak="break-word"
                {...highlightProps(
                  previewData.isFieldHighlighted?.('centralTitle')
                )}
              >
                {previewData.centralTitle || 'Google Pass Title'}
              </Text>
            )}
          </Flex>

          {/* NFC BADGE */}

          <Box h="0px" />

          {previewData.googleNfcEnabled && (
            <GoogleNfcBadge
              contrastColor={getContrastColor}
              isHighlighted={
                !!previewData.isFieldHighlighted?.('nfc.googleNfc')
              }
            />
          )}

          {/* BARCODE */}
          {createTemplate &&
            previewBarcodeImage &&
            !!previewData.barcode?.enabled &&
            !!previewData.barcode?.type &&
            !previewData.googleNfcEnabled && (
              <>
                <Flex
                  mt="6px"
                  w="full"
                  justifyContent="center"
                  data-highlighted={isBarcodeFieldHighlighted || undefined}
                >
                  <Flex
                    bg="white"
                    mt="6px"
                    py="10px"
                    px="10px"
                    borderRadius="10px"
                    w="fit-content"
                    justifyContent="center"
                    alignItems="center"
                    boxShadow={
                      isBarcodeFieldHighlighted
                        ? `0 0 0 2px ${HIGHLIGHT_COLOR}`
                        : 'none'
                    }
                  >
                    <BarcodePreview
                      type={previewData.barcode.type}
                      value={previewData.barcode.value}
                    />
                  </Flex>
                </Flex>

                {/* BARCODE DISPLAY TEXT — always shown on Google (Google bug workaround) */}
                {(previewData.barcode?.source !== 'custom' ||
                  (previewData.barcode?.source === 'custom' &&
                    previewData.barcode?.value)) && (
                  <>
                    <Flex w="full" justifyContent="center">
                      <Text
                        fontSize="10px"
                        fontWeight="600"
                        textStyle="headingFamilySemiBoldGooglePay"
                        mt="3px"
                        mb={1}
                        color={getContrastColor}
                        borderRadius="4px"
                        border="none"
                        p="1"
                      >
                        {previewData.barcode.value !== ''
                          ? previewData.barcode.value
                          : previewData.barcode.source === 'custom'
                            ? ''
                            : previewData.barcode.source?.toUpperCase()}
                      </Text>
                    </Flex>
                  </>
                )}
              </>
            )}

          <Box h="8px" />

          {/* HEADER ONE */}
          {(previewData.passType === 'loyalty' ||
            previewData.passType === 'stampCard' ||
            previewData.passType === 'eventTicket') && (
            <Box>
              <Flex
                width="full"
                direction="column"
                alignItems={detailsFieldsPresent ? 'flex-start' : 'center'}
                pl={detailsFieldsPresent ? '22px' : undefined}
                pt={
                  previewData.centralTitle &&
                  (previewData.passType === 'loyalty' ||
                    previewData.passType === 'stampCard' ||
                    previewData.passType === 'eventTicket')
                    ? '3'
                    : '2'
                }
              >
                <GooglePassField
                  valueFontWeight="600"
                  hideIdPlaceholder={previewData.hideIdPlaceholder}
                  label={previewData.headerOneLabel || ''}
                  value={previewData.headerOneValue}
                  id={previewData.headerOneId}
                  color={getContrastColor}
                  labelFontSize="11px"
                  valueFontSize="13px"
                  textAlign={detailsFieldsPresent ? 'left' : 'center'}
                  wordBreak="break-word"
                  isHighlighted={
                    (previewData.headerOneId ||
                      previewData.headerOneLabel ||
                      previewData.headerOneValue) &&
                    (previewData.isFieldHighlighted?.('header.one.id') ||
                      previewData.isFieldHighlighted?.('header.one.label') ||
                      previewData.isFieldHighlighted?.(
                        'header.one.defaultValue'
                      ))
                  }
                />
              </Flex>
            </Box>
          )}

          {/* PRIMARY ONE - now shown in central title position for generic */}

          {/* TRANSIT COMPONENTS */}
          {previewData.passType === 'transit' && (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                mt={2}
                position="relative"
              >
                {/* PRIMARY ONE (TRANSIT) */}
                <Flex
                  width="full"
                  alignItems="flex-start"
                  pl="18px"
                  maxWidth="47%"
                >
                  <GooglePassField
                    valueFontWeight="600"
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={undefined}
                    value={previewData.primaryOneValue}
                    id={previewData.primaryOneId}
                    color={getContrastColor}
                    valueFontSize="16px"
                    mtWhenNoLabel="0"
                    wordBreak="break-word"
                    isHighlighted={
                      (previewData.primaryOneId ||
                        previewData.primaryOneValue) &&
                      (previewData.isFieldHighlighted?.('central.one.id') ||
                        previewData.isFieldHighlighted?.(
                          'central.one.defaultValue'
                        ))
                    }
                  />
                </Flex>

                {/* ARROW ICON */}
                <Box
                  position="absolute"
                  left="50%"
                  top="50%"
                  transform="translate(-50%, -50%)"
                  pointerEvents="none"
                  zIndex={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ArrowForwardIcon fontSize="15px" color={getContrastColor} />
                </Box>

                {/* PRIMARY TWO */}
                <Flex width="full" pl="12px" pr="10px" maxWidth="47%">
                  <GooglePassField
                    valueFontWeight="600"
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={undefined}
                    value={previewData.primaryTwoValue}
                    id={previewData.primaryTwoId}
                    color={getContrastColor}
                    valueFontSize="16px"
                    mtWhenNoLabel="0"
                    wordBreak="break-word"
                    isHighlighted={
                      (previewData.primaryTwoId ||
                        previewData.primaryTwoValue) &&
                      (previewData.isFieldHighlighted?.('central.two.id') ||
                        previewData.isFieldHighlighted?.(
                          'central.two.defaultValue'
                        ))
                    }
                  />
                </Flex>
              </Flex>
              {/* HEADER ONE */}
              <Box>
                <Flex
                  width="full"
                  direction="column"
                  alignItems="flex-start"
                  pl="22px"
                  pt={3}
                >
                  <GooglePassField
                    valueFontWeight="600"
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={previewData.headerOneLabel || ''}
                    value={previewData.headerOneValue}
                    id={previewData.headerOneId}
                    color={getContrastColor}
                    labelFontSize="11px"
                    valueFontSize="13px"
                    textAlign="left"
                    wordBreak="break-word"
                    isHighlighted={
                      (previewData.headerOneId ||
                        previewData.headerOneLabel ||
                        previewData.headerOneValue) &&
                      (previewData.isFieldHighlighted?.('header.one.id') ||
                        previewData.isFieldHighlighted?.('header.one.label') ||
                        previewData.isFieldHighlighted?.(
                          'header.one.defaultValue'
                        ))
                    }
                  />
                </Flex>
              </Box>
            </>
          )}

          {/* DETAILS FIELDS WRAPPER */}
          <Box
            borderBottomRadius={'18px'}
            bg={backgroundColor}
            overflow="hidden"
            position="relative"
            zIndex={2}
            pb="28px"
            mb={shouldShowBanner || showsBannerPlaceholder ? '-14px' : '0'}
          >
            {/* SPACER before details */}
            {secValuesPresent && <Box mt={3} />}

            {/* DETAILS FIELDS */}
            <Flex
              justifyContent={singleDetailGeneric ? 'center' : 'space-between'}
              pl={singleDetailGeneric ? undefined : '16px'}
              pr={singleDetailGeneric ? undefined : '20px'}
              mt={3}
            >
              {/* DETAILS ONE */}
              {(!!previewData.detailsOneId ||
                !!previewData.detailsOneLabel ||
                !!previewData.detailsOneValue ||
                !!previewData.isFieldHighlighted?.('details.one.id') ||
                !!previewData.isFieldHighlighted?.('details.one.label') ||
                !!previewData.isFieldHighlighted?.(
                  'details.one.defaultValue'
                )) && (
                <Flex
                  minW="50px"
                  pl={singleDetailGeneric ? 0 : 2}
                  maxW={singleDetailGeneric ? undefined : '100px'}
                  overflow="hidden"
                  direction="column"
                  alignItems={singleDetailGeneric ? 'center' : 'flex-start'}
                >
                  <GooglePassField
                    valueFontWeight="600"
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={previewData.detailsOneLabel || ''}
                    value={previewData.detailsOneValue}
                    id={previewData.detailsOneId}
                    color={getContrastColor}
                    textAlign={singleDetailGeneric ? 'center' : 'left'}
                    isHighlighted={
                      !!previewData.isFieldHighlighted?.('details.one.id') ||
                      !!previewData.isFieldHighlighted?.('details.one.label') ||
                      !!previewData.isFieldHighlighted?.(
                        'details.one.defaultValue'
                      )
                    }
                    mtWhenNoLabel="15px"
                  />
                </Flex>
              )}
              {/* DETAILS TWO */}
              {(!!previewData.detailsTwoId ||
                !!previewData.detailsTwoLabel ||
                !!previewData.detailsTwoValue ||
                !!previewData.isFieldHighlighted?.('details.two.id') ||
                !!previewData.isFieldHighlighted?.('details.two.label') ||
                !!previewData.isFieldHighlighted?.(
                  'details.two.defaultValue'
                )) && (
                <Flex
                  minW="50px"
                  pl={singleDetailGeneric ? 0 : 2}
                  maxW={singleDetailGeneric ? undefined : '100px'}
                  overflow="hidden"
                  direction="column"
                  alignItems={singleDetailGeneric ? 'center' : mapAlignToJustify(detailsTwoAlignment)}
                >
                  <GooglePassField
                    valueFontWeight="600"
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={previewData.detailsTwoLabel || ''}
                    value={previewData.detailsTwoValue}
                    id={previewData.detailsTwoId}
                    color={getContrastColor}
                    isHighlighted={
                      !!previewData.isFieldHighlighted?.('details.two.id') ||
                      !!previewData.isFieldHighlighted?.('details.two.label') ||
                      !!previewData.isFieldHighlighted?.(
                        'details.two.defaultValue'
                      )
                    }
                    textAlign={singleDetailGeneric ? 'center' : detailsTwoAlignment}
                    mtWhenNoLabel="15px"
                  />
                </Flex>
              )}

              {/* DETAILS THREE */}
              {(!!previewData.detailsThreeId ||
                !!previewData.detailsThreeLabel ||
                !!previewData.detailsThreeValue ||
                !!previewData.isFieldHighlighted?.('details.three.id') ||
                !!previewData.isFieldHighlighted?.('details.three.label') ||
                !!previewData.isFieldHighlighted?.(
                  'details.three.defaultValue'
                )) && (
                <Flex
                  minW="50px"
                  pl={singleDetailGeneric ? 0 : 2}
                  maxW={singleDetailGeneric ? undefined : '100px'}
                  overflow="hidden"
                  direction="column"
                  alignItems={singleDetailGeneric ? 'center' : mapAlignToJustify(detailsThreeAlignment)}
                >
                  <GooglePassField
                    valueFontWeight="600"
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    label={previewData.detailsThreeLabel || ''}
                    value={previewData.detailsThreeValue}
                    id={previewData.detailsThreeId}
                    color={getContrastColor}
                    isHighlighted={
                      !!previewData.isFieldHighlighted?.('details.three.id') ||
                      !!previewData.isFieldHighlighted?.(
                        'details.three.label'
                      ) ||
                      !!previewData.isFieldHighlighted?.(
                        'details.three.defaultValue'
                      )
                    }
                    textAlign={singleDetailGeneric ? 'center' : detailsThreeAlignment}
                    mtWhenNoLabel="15px"
                  />
                </Flex>
              )}
            </Flex>

            {/* 2ND ROW FIELDS */}
            {auxValuesPresent && (
              <>
                {/* SPACER before 2nd row */}
                <Box mt={secValuesPresent ? '3' : '0'} />
                <Flex justifyContent="space-between" pl="16px" pr="20px" mt={3}>
                  {/* DETAILS FOUR */}
                  {(!!previewData.detailsFourId ||
                    !!previewData.detailsFourLabel ||
                    !!previewData.detailsFourValue ||
                    !!previewData.isFieldHighlighted?.('details.four.id') ||
                    !!previewData.isFieldHighlighted?.('details.four.label') ||
                    !!previewData.isFieldHighlighted?.(
                      'details.four.defaultValue'
                    )) && (
                    <Box
                      flex="1"
                      minW="50px"
                      maxW="100px"
                      overflow="hidden"
                      pl={2}
                    >
                      <GooglePassField
                        valueFontWeight="600"
                        hideIdPlaceholder={previewData.hideIdPlaceholder}
                        label={previewData.detailsFourLabel || ''}
                        value={previewData.detailsFourValue}
                        id={previewData.detailsFourId}
                        color={getContrastColor}
                        isHighlighted={
                          !!previewData.isFieldHighlighted?.(
                            'details.four.id'
                          ) ||
                          !!previewData.isFieldHighlighted?.(
                            'details.four.label'
                          ) ||
                          !!previewData.isFieldHighlighted?.(
                            'details.four.defaultValue'
                          )
                        }
                        mtWhenNoLabel="15px"
                      />
                    </Box>
                  )}
                  {/* DETAILS FIVE */}
                  {previewData.passType !== 'loyalty' &&
                    previewData.passType !== 'stampCard' && (
                      <>
                        {(!!previewData.detailsFiveId ||
                          !!previewData.detailsFiveLabel ||
                          !!previewData.detailsFiveValue ||
                          !!previewData.isFieldHighlighted?.(
                            'details.five.id'
                          ) ||
                          !!previewData.isFieldHighlighted?.(
                            'details.five.label'
                          ) ||
                          !!previewData.isFieldHighlighted?.(
                            'details.five.defaultValue'
                          )) && (
                          <Box
                            flex="1"
                            minW="50px"
                            maxW="100px"
                            overflow="hidden"
                            pl={2}
                            display="flex"
                            justifyContent={mapAlignToJustify(
                              detailsFiveAlignment
                            )}
                          >
                            <GooglePassField
                              valueFontWeight="600"
                              hideIdPlaceholder={previewData.hideIdPlaceholder}
                              label={previewData.detailsFiveLabel || ''}
                              value={previewData.detailsFiveValue}
                              id={previewData.detailsFiveId}
                              color={getContrastColor}
                              isHighlighted={
                                !!previewData.isFieldHighlighted?.(
                                  'details.five.id'
                                ) ||
                                !!previewData.isFieldHighlighted?.(
                                  'details.five.label'
                                ) ||
                                !!previewData.isFieldHighlighted?.(
                                  'details.five.defaultValue'
                                )
                              }
                              mtWhenNoLabel="15px"
                              textAlign={detailsFiveAlignment}
                            />
                          </Box>
                        )}

                        {/* DETAILS SIX */}
                        {(!!previewData.detailsSixId ||
                          !!previewData.detailsSixLabel ||
                          !!previewData.detailsSixValue ||
                          !!previewData.isFieldHighlighted?.(
                            'details.six.id'
                          ) ||
                          !!previewData.isFieldHighlighted?.(
                            'details.six.label'
                          ) ||
                          !!previewData.isFieldHighlighted?.(
                            'details.six.defaultValue'
                          )) && (
                          <Box
                            flex="1"
                            minW="50px"
                            maxW="100px"
                            overflow="hidden"
                            pl={2}
                            display="flex"
                            justifyContent={mapAlignToJustify(
                              detailsSixAlignment
                            )}
                          >
                            <GooglePassField
                              valueFontWeight="600"
                              hideIdPlaceholder={previewData.hideIdPlaceholder}
                              label={previewData.detailsSixLabel || ''}
                              value={previewData.detailsSixValue}
                              id={previewData.detailsSixId}
                              color={getContrastColor}
                              isHighlighted={
                                !!previewData.isFieldHighlighted?.(
                                  'details.six.id'
                                ) ||
                                !!previewData.isFieldHighlighted?.(
                                  'details.six.label'
                                ) ||
                                !!previewData.isFieldHighlighted?.(
                                  'details.six.defaultValue'
                                )
                              }
                              mtWhenNoLabel="15px"
                              textAlign={detailsSixAlignment}
                            />
                          </Box>
                        )}
                      </>
                    )}
                </Flex>
                <Box h="28px" />
              </>
            )}

            {hasAppLink && (
              <GoogleAppLinkButton
                href={getGoogleAppLinkHref(previewData)}
                displayText={previewData.googleAppLinkDisplayText}
                hasApp={
                  !!(
                    previewData.googleAppLinkPlayStoreUrl ||
                    previewData.googleAppLinkPackageName
                  )
                }
                contrastColor={getContrastColor}
                isHighlighted={
                  !!(
                    previewData.isFieldHighlighted?.(
                      'appLinking.googleAppPlayStoreUrl'
                    ) ||
                    previewData.isFieldHighlighted?.(
                      'appLinking.googleAppPackageName'
                    ) ||
                    previewData.isFieldHighlighted?.(
                      'appLinking.googleWebUrl'
                    ) ||
                    previewData.isFieldHighlighted?.(
                      'appLinking.googleDisplayText'
                    )
                  )
                }
              />
            )}
          </Box>
        </Box>

        {/* BANNER */}
        {shouldShowBanner ? (
          (() => {
            const bannerSrc =
              !!previewData.passDesign &&
              !!previewData.passDesign.bannerImageUrl &&
              previewData.passType !== 'generic' &&
              previewData.passType !== 'transit'
                ? previewData.passDesign.bannerImageUrl
                : previewData.googleBannerImage
                  ? previewData.googleBannerImage
                  : previewData.bannerImage;
            return (
              <Box
                h="auto"
                w="full"
                bg={backgroundColor}
                position="relative"
                zIndex={0}
                flexShrink={0}
                overflow="visible"
                onMouseEnter={() => setIsBannerHovered(true)}
                onMouseLeave={() => setIsBannerHovered(false)}
                data-highlighted={
                  previewData.isFieldHighlighted?.('design.shared.banner') ||
                  previewData.isFieldHighlighted?.('design.google.banner') ||
                  previewData.isStampBannerHighlighted ||
                  undefined
                }
              >
                {/* Mirrored banner above — peeks through top box's rounded corners */}
                <Box
                  position="absolute"
                  bottom="100%"
                  left={0}
                  w="full"
                  h="20px"
                  pointerEvents="none"
                  style={{
                    backgroundImage: `url(${bannerSrc})`,
                    backgroundSize: '100% auto',
                    backgroundPosition: 'top',
                    backgroundRepeat: 'no-repeat',
                    transform: 'scaleY(-1)',
                  }}
                />
                {/* Mirrored banner below — peeks through bottom box's rounded corners */}
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  w="full"
                  h="20px"
                  pointerEvents="none"
                  style={{
                    backgroundImage: `url(${bannerSrc})`,
                    backgroundSize: '100% auto',
                    backgroundPosition: 'bottom',
                    backgroundRepeat: 'no-repeat',
                    transform: 'scaleY(-1)',
                  }}
                />
                <Box position="relative">
                  <Image
                    w="full"
                    h="auto"
                    src={bannerSrc}
                    alt=""
                    aspectRatio={googleBannerAspectRatio}
                    display="block"
                  />
                  {(previewData.isFieldHighlighted?.('design.shared.banner') ||
                    previewData.isFieldHighlighted?.('design.google.banner') ||
                    previewData.isStampBannerHighlighted) && (
                    <Box
                      position="absolute"
                      inset={0}
                      border={`2px solid ${HIGHLIGHT_COLOR}`}
                      borderRadius="4px"
                      pointerEvents="none"
                    />
                  )}
                </Box>

                {isDefaultWaveBanner && (
                  <Image
                    src={googlePlaceholderText}
                    alt=""
                    position="absolute"
                    top="78%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    w="70%"
                    opacity={0.5}
                    pointerEvents="none"
                  />
                )}
                {!isBannerHovered && (
                  <TrialBannerOverlay isInTrial={isInTrial} />
                )}
                {/* WATERMARK */}
                {createTemplate &&
                  watermarkEnabled &&
                  (!previewData.googleBannerImage
                    ? previewData.displayBannerWatermarkPreview
                    : previewData.displayGoogleBannerWatermarkPreview) && (
                    <Box
                      position="absolute"
                      top={
                        (watermarkPosition === 'topLeft' ||
                          watermarkPosition === 'topRight') &&
                        '0'
                      }
                      bottom={
                        (watermarkPosition === 'bottomLeft' ||
                          watermarkPosition === 'bottomRight') &&
                        '0'
                      }
                      left={
                        (watermarkPosition === 'topLeft' ||
                          watermarkPosition === 'bottomLeft') &&
                        '0'
                      }
                      right={
                        (watermarkPosition === 'topRight' ||
                          watermarkPosition === 'bottomRight') &&
                        '0'
                      }
                      mx="9px"
                      my="5px"
                      boxShadow={
                        isWatermarkFieldHighlighted
                          ? `0 0 0 2px ${HIGHLIGHT_COLOR}`
                          : 'none'
                      }
                    >
                      <Image
                        src={
                          watermarkColor === 'light'
                            ? previewWatermarkLight
                            : previewWatermarkDark
                        }
                        alt="watermark"
                        w="112px"
                        h="18.5px"
                      />
                    </Box>
                  )}
              </Box>
            );
          })()
        ) : showsBannerPlaceholder ? (
          <Box
            h="auto"
            w="full"
            position="relative"
            zIndex={0}
            flexShrink={0}
            overflow="visible"
          >
            <ImagePlaceholder
              position="absolute"
              bottom="100%"
              left={0}
              w="full"
              h="20px"
              pointerEvents="none"
            />
            <ImagePlaceholder
              position="absolute"
              top="100%"
              left={0}
              w="full"
              h="20px"
              pointerEvents="none"
            />
            <ImagePlaceholder
              w="full"
              h="auto"
              aspectRatio={googleBannerAspectRatio}
              boxShadow={
                previewData.isFieldHighlighted?.('design.shared.banner') ||
                previewData.isFieldHighlighted?.('design.google.banner')
                  ? `inset 0 0 0 2px ${HIGHLIGHT_COLOR}`
                  : 'none'
              }
            />
          </Box>
        ) : (
          <Box h="20px" flexShrink={0} />
        )}

        {/* BELOW BANNER SECTION */}
        <Box
          borderTopRadius={'18px'}
          bg={backgroundColor}
          position="relative"
          zIndex={2}
          flex="1"
          mt={shouldShowBanner || showsBannerPlaceholder ? '1px' : '0'}
        >
          {/* KEY FIELDS + ADDITIONAL/BACK FIELDS */}
          {createTemplate &&
            (() => {
              const keyFields = [
                { prefix: 'headerOne', section: 'header', slot: 'one' },
                { prefix: 'headerTwo', section: 'header', slot: 'two' },
                { prefix: 'detailsOne', section: 'details', slot: 'one' },
                { prefix: 'detailsTwo', section: 'details', slot: 'two' },
                { prefix: 'detailsThree', section: 'details', slot: 'three' },
                { prefix: 'detailsFour', section: 'details', slot: 'four' },
                { prefix: 'detailsFive', section: 'details', slot: 'five' },
                { prefix: 'detailsSix', section: 'details', slot: 'six' },
              ];
              const visibleKeyFields = keyFields.filter(({ prefix }) => {
                const id = previewData[
                  `${prefix}Id` as keyof PreviewData
                ] as string;
                const label = previewData[
                  `${prefix}Label` as keyof PreviewData
                ] as string;
                const value = previewData[
                  `${prefix}Value` as keyof PreviewData
                ] as string;
                return !!(id || label || value);
              });

              const allFields = [
                { prefix: 'backOne', slot: 'one' },
                { prefix: 'backTwo', slot: 'two' },
                { prefix: 'backThree', slot: 'three' },
                { prefix: 'backFour', slot: 'four' },
                { prefix: 'backFive', slot: 'five' },
              ];
              const visibleFields = allFields.filter(({ prefix, slot }) => {
                const id = previewData[
                  `${prefix}Id` as keyof PreviewData
                ] as string;
                const label = previewData[
                  `${prefix}Label` as keyof PreviewData
                ] as string;
                const value = previewData[
                  `${prefix}Value` as keyof PreviewData
                ] as string;
                const isHighlighted =
                  previewData.isFieldHighlighted?.(`back.${slot}.id`) ||
                  previewData.isFieldHighlighted?.(`back.${slot}.label`) ||
                  previewData.isFieldHighlighted?.(`back.${slot}.defaultValue`);
                return !!(label || value || isHighlighted);
              });
              const allVisibleCount =
                visibleKeyFields.length + visibleFields.length;
              const totalItems = allVisibleCount + 1; // +1 for terms
              return (
                <Box
                  px="15px"
                  pb="10px"
                  pt="22px"
                  data-section="additional-fields"
                >
                  <Box bg={lightenedBg} borderRadius="10px" overflow="hidden">
                    {/* KEY FIELDS */}
                    {visibleKeyFields.map(({ prefix, section, slot }, idx) => {
                      const id = previewData[
                        `${prefix}Id` as keyof PreviewData
                      ] as string;
                      const label = previewData[
                        `${prefix}Label` as keyof PreviewData
                      ] as string;
                      const value = previewData[
                        `${prefix}Value` as keyof PreviewData
                      ] as string;
                      const isHighlighted =
                        previewData.isFieldHighlighted?.(
                          `${section}.${slot}.id`
                        ) ||
                        previewData.isFieldHighlighted?.(
                          `${section}.${slot}.label`
                        ) ||
                        previewData.isFieldHighlighted?.(
                          `${section}.${slot}.defaultValue`
                        );
                      const isFirst = idx === 0;

                      return (
                        <Box
                          key={prefix}
                          position="relative"
                          px="12px"
                          py="12px"
                          borderTop={
                            !isFirst ? `2px solid ${backgroundColor}` : 'none'
                          }
                          borderTopRadius={isFirst ? '10px' : undefined}
                          minH="28px"
                          data-highlighted={isHighlighted || undefined}
                        >
                          {isHighlighted && (
                            <Box
                              position="absolute"
                              inset={0}
                              border={`2px solid ${HIGHLIGHT_COLOR}`}
                              borderRadius={
                                isFirst ? '10px 10px 4px 4px' : '4px'
                              }
                              pointerEvents="none"
                            />
                          )}
                          {label && (
                            <Text
                              fontSize="12px"
                              textStyle="headingFamilySemiBoldGooglePay"
                              color={getContrastColor}
                              pb="2px"
                            >
                              {label}
                            </Text>
                          )}
                          {value ? (
                            <Text
                              fontSize="12px"
                              textStyle="headingFamilyRegularGooglePay"
                              color={getContrastColor}
                              wordBreak="break-word"
                            >
                              {value}
                            </Text>
                          ) : id && !previewData.hideIdPlaceholder ? (
                            <Text
                              fontSize="12px"
                              textStyle="headingFamilyRegularGooglePay"
                              color={getContrastColor}
                              wordBreak="break-word"
                            >
                              {'<' + id + '>'}
                            </Text>
                          ) : null}
                        </Box>
                      );
                    })}

                    {/* ADDITIONAL/BACK FIELDS */}
                    {visibleFields.map(({ prefix, slot }, idx) => {
                      const globalIdx = visibleKeyFields.length + idx;
                      const id = previewData[
                        `${prefix}Id` as keyof PreviewData
                      ] as string;
                      const label = previewData[
                        `${prefix}Label` as keyof PreviewData
                      ] as string;
                      const value = previewData[
                        `${prefix}Value` as keyof PreviewData
                      ] as string;
                      const isHighlighted =
                        previewData.isFieldHighlighted?.(`back.${slot}.id`) ||
                        previewData.isFieldHighlighted?.(
                          `back.${slot}.label`
                        ) ||
                        previewData.isFieldHighlighted?.(
                          `back.${slot}.defaultValue`
                        );
                      const isFirst = globalIdx === 0;

                      return (
                        <Box
                          key={prefix}
                          position="relative"
                          px="12px"
                          py="12px"
                          borderTop={
                            !isFirst ? `2px solid ${backgroundColor}` : 'none'
                          }
                          minH="28px"
                          data-highlighted={isHighlighted || undefined}
                        >
                          {isHighlighted && (
                            <Box
                              position="absolute"
                              inset={0}
                              border={`2px solid ${HIGHLIGHT_COLOR}`}
                              borderRadius="4px"
                              pointerEvents="none"
                            />
                          )}
                          {(() => {
                            const lines = value ? value.split('\\n') : [];
                            const isCollapsible = lines.length > 2;
                            const isExpanded = expandedFields[prefix];
                            const displayLines =
                              isCollapsible && !isExpanded
                                ? lines.slice(0, 2)
                                : lines;
                            return (
                              <>
                                {label && (
                                  <Flex
                                    justify="space-between"
                                    align="center"
                                    cursor={
                                      isCollapsible ? 'pointer' : undefined
                                    }
                                    onClick={
                                      isCollapsible
                                        ? () =>
                                            setExpandedFields((prev) => ({
                                              ...prev,
                                              [prefix]: !prev[prefix],
                                            }))
                                        : undefined
                                    }
                                  >
                                    <Text
                                      fontSize="12px"
                                      textStyle="headingFamilySemiBoldGooglePay"
                                      color={getContrastColor}
                                      pb="2px"
                                    >
                                      {label}
                                    </Text>
                                    {isCollapsible &&
                                      (isExpanded ? (
                                        <ChevronUpIcon
                                          boxSize="14px"
                                          color={getContrastColor}
                                        />
                                      ) : (
                                        <ChevronDownIcon
                                          boxSize="14px"
                                          color={getContrastColor}
                                        />
                                      ))}
                                  </Flex>
                                )}
                                {id &&
                                !value &&
                                !previewData.hideIdPlaceholder ? (
                                  <Text
                                    fontSize="12px"
                                    textStyle="headingFamilyRegularGooglePay"
                                    color={getContrastColor}
                                    wordBreak="break-word"
                                  >
                                    {'<' + id + '>'}
                                  </Text>
                                ) : value ? (
                                  <Box
                                    cursor={
                                      isCollapsible ? 'pointer' : undefined
                                    }
                                    onClick={
                                      isCollapsible
                                        ? () =>
                                            setExpandedFields((prev) => ({
                                              ...prev,
                                              [prefix]: !prev[prefix],
                                            }))
                                        : undefined
                                    }
                                  >
                                    {displayLines.map((line, i) => (
                                      <Text
                                        key={i}
                                        fontSize="12px"
                                        textStyle="headingFamilyRegularGooglePay"
                                        color={getContrastColor}
                                        wordBreak="break-word"
                                        sx={{
                                          '& a': {
                                            color: `${getContrastColor} !important`,
                                            textDecoration:
                                              'underline !important',
                                            textUnderlineOffset: '2px',
                                          },
                                        }}
                                      >
                                        {parseString(line)}
                                        {isCollapsible &&
                                        !isExpanded &&
                                        i === displayLines.length - 1
                                          ? '...'
                                          : ''}
                                      </Text>
                                    ))}
                                  </Box>
                                ) : null}
                              </>
                            );
                          })()}
                        </Box>
                      );
                    })}

                    {/* TERMS */}
                    <Box
                      px="12px"
                      py="12px"
                      borderTop={
                        allVisibleCount > 0
                          ? `2px solid ${backgroundColor}`
                          : 'none'
                      }
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        cursor="pointer"
                        onClick={() => setTermsOpen(!termsOpen)}
                      >
                        <Text
                          fontSize="12px"
                          textStyle="headingFamilySemiBoldGooglePay"
                          color={getContrastColor}
                        >
                          Terms
                        </Text>
                        {termsOpen ? (
                          <ChevronUpIcon
                            boxSize="14px"
                            color={getContrastColor}
                          />
                        ) : (
                          <ChevronDownIcon
                            boxSize="14px"
                            color={getContrastColor}
                          />
                        )}
                      </Flex>
                      <Box mt="4px">
                        <Text
                          fontSize="10px"
                          textStyle="headingFamilyRegularGooglePay"
                          color={getContrastColor}
                          opacity={0.7}
                          pb={termsOpen ? '8px' : '0'}
                          noOfLines={termsOpen ? undefined : 2}
                          maxW={termsOpen ? undefined : '85%'}
                        >
                          Your use of the Google Wallet is subject to Google's
                          terms and conditions of use, which PassEntry are not
                          responsible for.
                        </Text>
                        {termsOpen && (
                          <Text
                            fontSize="10px"
                            textStyle="headingFamilyRegularGooglePay"
                            color={getContrastColor}
                            opacity={0.7}
                          >
                            This information is provided by PassEntry, the
                            provider of this pass. Google is not responsible for
                            the accuracy of this information.
                          </Text>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })()}

          {createTemplate && (
            <GooglePassFooter
              contrastColor={getContrastColor}
              backgroundColor={backgroundColor}
              lightenedBg={lightenedBg}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default memo(GoogleExpressivePassPreview);
